// MaterialGraphPalette.jsx — Left sidebar with searchable node palette.
// Uses design's .sg-palette classes.

import React, { useState, useRef, useEffect, useMemo } from 'react'
import { NODE_TYPES } from '../../lib/materialNodes'
import { categoryOf, CAT_COLORS } from './materialGraphTheme'
import './materialGraph.css'

export default function MaterialGraphPalette({ onAddNode }) {
  const [search, setSearch] = useState('')
  const inputRef = useRef(null)

  useEffect(() => {
    // Auto-focus on mount
    // (in a real modal, this might be called explicitly)
  }, [])

  // Build palette from NODE_TYPES, excluding output node
  const allItems = useMemo(() => {
    return Object.entries(NODE_TYPES)
      .filter(([, def]) => !def.isOutput)
      .map(([typeKey, def]) => ({
        type: typeKey,
        label: def.label,
        category: categoryOf(typeKey),
      }))
  }, [])

  // Filter by search
  const filtered = useMemo(() => {
    if (!search) return allItems
    const q = search.toLowerCase()
    return allItems.filter(
      item =>
        item.label.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q)
    )
  }, [search, allItems])

  // Group by category
  const grouped = useMemo(() => {
    const groups = {}
    for (const item of filtered) {
      const cat = item.category
      if (!groups[cat]) groups[cat] = []
      groups[cat].push(item)
    }
    // Order: texture, param, input, math, shader
    const order = ['texture', 'param', 'input', 'math', 'shader']
    return order
      .filter(cat => groups[cat])
      .map(cat => ({ category: cat, items: groups[cat] }))
  }, [filtered])

  return (
    <div className="sg-palette">
      {/* Search box */}
      <div className="sg-pal-search">
        <span className="sg-pal-search-ic">🔍</span>
        <input
          ref={inputRef}
          type="text"
          placeholder="Search nodes…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="sg-pal-search-input"
        />
      </div>

      {/* Scrollable palette */}
      <div className="sg-pal-scroll">
        {grouped.length === 0 && (
          <div className="sg-pal-empty">No matches</div>
        )}

        {grouped.map(group => (
          <div key={group.category} className="sg-pal-group">
            {/* Group header */}
            <div className="sg-pal-grouphd">
              {group.category}
            </div>

            {/* Items */}
            {group.items.map(item => (
              <button
                key={item.type}
                onClick={() => onAddNode(item.type)}
                className="sg-pal-item"
              >
                {/* Category color bar */}
                <span
                  className="sg-pal-bar"
                  style={{
                    background: CAT_COLORS[item.category] || CAT_COLORS.param,
                  }}
                />
                <span className="sg-pal-name">{item.label}</span>
                <span className="sg-pal-add">+</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
